/**
 * Detecção de suporte e contexto para Web Push.
 *
 * Push e Service Worker exigem contexto seguro (HTTPS ou localhost). No iOS, o
 * Web Push só funciona a partir do iOS 16.4 e com o site instalado na Tela de
 * Início (modo standalone).
 */
import { bufferParaBase64url } from './base64url';

/** Normaliza uma chave VAPID em base64url (sem padding). */
export const normalizarChaveVapid = (valor) =>
  String(valor || '')
    .trim()
    .replace(/=+$/, '');

/**
 * Chave VAPID usada pela inscrição atual do navegador (base64url), ou `null`
 * quando o navegador não expõe `options.applicationServerKey`.
 */
export const chaveVapidDaInscricao = (subscription) => {
  const chave = subscription?.options?.applicationServerKey;
  if (!chave) return null;

  try {
    return normalizarChaveVapid(bufferParaBase64url(chave));
  } catch {
    return null;
  }
};

/**
 * `true` quando a inscrição local foi criada com outra chave VAPID (ex.: o
 * servidor trocou as chaves): o envio passa a retornar 403 e a inscrição
 * precisa ser refeita. Sem acesso à chave local, assume que está correta.
 */
export const inscricaoComChaveAntiga = (subscription, publicKey) => {
  const chaveLocal = chaveVapidDaInscricao(subscription);
  if (!chaveLocal) return false;

  return chaveLocal !== normalizarChaveVapid(publicKey);
};

export const contextoAtual = () => {
  if (typeof window === 'undefined') return 'indefinido';
  return window.isSecureContext ? 'seguro' : 'inseguro';
};

export const pushSuportado = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  return Boolean(
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

export const permissoesDeNotificacao = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'indefinido';
  return window.Notification.permission;
};

export const ehIos = () => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent || '';
  const ipadComTouch =
    navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1;

  return /iPad|iPhone|iPod/i.test(userAgent) || ipadComTouch;
};

export const estaInstalado = () => {
  if (typeof window === 'undefined') return false;

  const standalone = window.navigator.standalone === true;
  const displayMode = window.matchMedia?.('(display-mode: standalone)')?.matches;

  return Boolean(standalone || displayMode);
};

export const precisaInstalarNoIos = () => ehIos() && !estaInstalado();

export const plataformaAtual = () => {
  if (typeof navigator === 'undefined') return 'outro';
  if (ehIos()) return 'ios';
  if (/Android/i.test(navigator.userAgent || '')) return 'android';
  if (/Windows|Macintosh|Linux|CrOS/i.test(navigator.userAgent || '')) return 'desktop';
  return 'outro';
};

/** Nome amigável para exibir na lista de dispositivos. */
export const nomeDispositivoAtual = () => {
  if (typeof navigator === 'undefined') return 'Dispositivo';

  const userAgent = navigator.userAgent || '';

  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (
    /iPad/i.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    return 'iPad';
  }
  if (/Android/i.test(userAgent)) {
    const modelo = userAgent.match(/Android[^;]*;\s*([^;)]+)/i)?.[1]?.trim();
    return modelo ? `Android - ${modelo}`.slice(0, 80) : 'Android';
  }
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Macintosh/i.test(userAgent)) return 'Mac';
  if (/Linux/i.test(userAgent)) return 'Linux';

  return 'Dispositivo';
};

const pushUtils = {
  pushSuportado,
  permissoesDeNotificacao,
  precisaInstalarNoIos,
  estaInstalado,
  plataformaAtual,
  nomeDispositivoAtual,
  normalizarChaveVapid,
  chaveVapidDaInscricao,
  inscricaoComChaveAntiga,
};

export default pushUtils;
